import { useMemo } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import {
  Box,
  Chip,
  FormHelperText,
  MenuItem,
  OutlinedInput,
  Select,
  Typography,
  Tooltip,
  IconButton,
  Checkbox,
  ListItemText,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import IosSwitch from "../../../../components/styled/Switch";
import FormField from "../../../../components/styled/FormField";
import { formatWhatsAppMessage } from "../../../../utils/common/utils";
import {
  DeleteIconButton,
  EyeIconButton,
} from "../../../../../assets/common-svg/CommonSvg";

const FORMATS = [
  "header",
  "size",
  "bold",
  "italic",
  "underline",
  "strike",
  "blockquote",
  "code-block",
  "list",
  "bullet",
  "indent",
  "link",
  "color",
  "background",
  "align",
];

const QuillToolbar = ({ id }) => (
  <div id={id}>
    <span className="ql-formats">
      <select className="ql-header" defaultValue="" title="Heading">
        <option value="1">Heading 1</option>
        <option value="2">Heading 2</option>
        <option value="3">Heading 3</option>
        <option value="">Normal</option>
      </select>
    </span>
    <span className="ql-formats">
      <select className="ql-size" defaultValue="" title="Font Size">
        <option value="small">Small</option>
        <option value="">Normal</option>
        <option value="large">Large</option>
        <option value="huge">Huge</option>
      </select>
    </span>
    <span className="ql-formats">
      <button className="ql-bold" title="Bold (Ctrl+B)" />
      <button className="ql-italic" title="Italic (Ctrl+I)" />
      <button className="ql-underline" title="Underline (Ctrl+U)" />
      <button className="ql-strike" title="Strikethrough" />
    </span>
    <span className="ql-formats">
      <select className="ql-color" title="Text Color" />
      <select className="ql-background" title="Background Color" />
    </span>
    <span className="ql-formats">
      <select className="ql-align" title="Text Align" />
    </span>
    <span className="ql-formats">
      <button className="ql-list" value="ordered" title="Ordered List" />
      <button className="ql-list" value="bullet" title="Bullet List" />
    </span>
    <span className="ql-formats">
      <button className="ql-blockquote" title="Blockquote" />
      <button className="ql-link" title="Insert Link" />
      <button className="ql-code-block" title="Code Block" />
    </span>
  </div>
);
const CARD_HEIGHT = 620;

const ChannelCard = ({
  disableRole = false,
  channelKey,
  label,
  enabled,
  onToggle,
  messageField,
  subjectField,
  roleField,
  formik,
  quillRef,
  onEditorFocus,
  assignedVariables = [],
  onVariableClick,
  onVariableRemove,
  roleOptions = [],
  whatsappTemplateOptions = [],
  fileInputRef,
  setPreviewOpen,
  setPreviewFileUrl,
  setIsMediaDeleted,
}) => {
  const toolbarId = `ql-toolbar-${channelKey}`;
  const hasFile = Boolean(formik.values.files);
  const modules = useMemo(() => {
    const config = {
      toolbar: channelKey === "in_app" ? false : { container: `#${toolbarId}` },
      clipboard: { matchVisual: false },
    };

    if (channelKey === "in_app") {
      config.keyboard = {
        bindings: {
          bold: {
            key: "B",
            shortKey: true,
            handler: () => false,
          },
          italic: {
            key: "I",
            shortKey: true,
            handler: () => false,
          },
          underline: {
            key: "U",
            shortKey: true,
            handler: () => false,
          },
          enter: {
            key: "Enter",
            handler: () => false,
          },
          shiftEnter: {
            key: "Enter",
            shiftKey: true,
            handler: () => false,
          },
        },
      };
    }

    return config;
  }, [channelKey, toolbarId]);

  const selectedValues = useMemo(() => {
    const val = formik.values[roleField];
    if (!val || (Array.isArray(val) && val.length === 0)) return [];
    return Array.isArray(val) ? val : [val];
  }, [formik.values[roleField], roleField]);

  const whatsAppVariables = useMemo(
    () =>
      channelKey === "whatsapp"
        ? formik.values.whatsapp_template_variables || []
        : [],
    [channelKey, formik.values.whatsapp_template_variables],
  );

  const handleRoleChange = (event) => {
    const { value } = event.target;
    if (value.includes("ALL")) {
      const allSelected = roleOptions.every((opt) =>
        formik.values[roleField]?.includes(opt.value),
      );
      formik.setFieldValue(
        roleField,
        allSelected ? [] : roleOptions.map((opt) => opt.value),
      );
    } else {
      formik.setFieldValue(
        roleField,
        typeof value === "string" ? value.split(",") : value,
      );
    }
  };

  const handleRemoveRole = (roleValue) => {
    formik.setFieldValue(
      roleField,
      selectedValues.filter((v) => String(v) !== String(roleValue)),
    );
  };
  const fileDisplayName = useMemo(() => {
    const f = formik.values.files;
    if (!f) return null;
    if (f instanceof File) return f.name;
    return (
      f.file_name || f.name || f.original_name || f.media_url || "Attached file"
    );
  }, [formik.values.files]);

  const isExistingFile = Boolean(
    formik.values.files && !(formik.values.files instanceof File),
  );

  const getFileUrl = () => {
    const f = formik.values.files;
    if (!f) return "";
    if (f instanceof File) return URL.createObjectURL(f);
    const url = f.media_url || f.file_url || f.url || "";
    if (!url)
      console.warn("ChannelCard: existing file object has no URL key", f);
    return url;
  };

  const handleRemoveFile = () => {
    formik.setFieldValue("files", null);

    if (isExistingFile) {
      setIsMediaDeleted(true);
    }

    if (fileInputRef?.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <Box
      sx={{
        border: "1px solid #e5e7eb", // softer border like UI
        borderRadius: "12px",
        overflow: "hidden",
        backgroundColor: "#fafafa", // light grey card background
        display: "flex",
        flexDirection: "column",
        height: CARD_HEIGHT,
        width: "100%",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          py: 1.25,
          backgroundColor: "#f3f4f6", // light grey header
          borderBottom: "1px solid #e5e7eb",
          flexShrink: 0,
        }}
      >
        <Typography variant="subtitle2" fontWeight={600} fontSize="0.95rem">
          {label}
        </Typography>
        <Tooltip
          title={!enabled ? `Click to enable ${label} channel` : ""}
          placement="top"
          arrow
        >
          <span>
            <IosSwitch checked={enabled} onChange={onToggle} />
          </span>
        </Tooltip>
      </Box>

      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          p: 2,
          display: "flex",
          flexDirection: "column",
          gap: 2,
          backgroundColor: "#ffffff",
          opacity: enabled ? 1 : 0.5,
          pointerEvents: enabled ? "auto" : "none",
          userSelect: enabled ? "auto" : "none",
        }}
      >
        <Box>
          <Typography variant="body2" fontWeight={500} mb={0.5}>
            Role Permission
          </Typography>
          <Select
            multiple
            fullWidth
            size="small"
            displayEmpty
            value={selectedValues}
            disabled={disableRole || !enabled}
            onChange={handleRoleChange}
            input={<OutlinedInput />}
            renderValue={(selected) => {
              if (selected.length === 0) {
                return (
                  <Typography variant="body2" color="text.disabled">
                    Select Role
                  </Typography>
                );
              }

              return (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {selected.map((val) => {
                    const opt = roleOptions.find(
                      (o) => String(o.value) === String(val),
                    );
                    return (
                      <Chip
                        key={val}
                        label={opt?.label ?? val}
                        size="small"
                        onDelete={() => handleRemoveRole(val)}
                        onMouseDown={(e) => e.stopPropagation()}
                        sx={{
                          backgroundColor: "#eff6ff",
                          border: "1px solid #bfdbfe",
                          color: "primary.main",
                          fontWeight: 600,
                          fontSize: "0.7rem",
                          "& .MuiChip-deleteIcon": {
                            color: "primary.main",
                            "&:hover": { color: "#ef4444" },
                          },
                        }}
                      />
                    );
                  })}
                </Box>
              );
            }}
            MenuProps={{ PaperProps: { sx: { maxHeight: 240 } } }}
            sx={{
              backgroundColor: "#f9fafb",
              borderRadius: "6px",
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "#e5e7eb",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "#cbd5e1",
              },
            }}
          >
            <MenuItem value="ALL">
              <Checkbox
                checked={selectedValues.length === roleOptions.length}
                indeterminate={
                  selectedValues.length > 0 &&
                  selectedValues.length < roleOptions.length
                }
              />
              <ListItemText primary="Select All" />
            </MenuItem>
            {roleOptions.length === 0 ? (
              <MenuItem disabled>
                <Typography variant="body2" color="text.disabled">
                  No roles available
                </Typography>
              </MenuItem>
            ) : (
              roleOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))
            )}
          </Select>

          {enabled && formik.touched[roleField] && formik.errors[roleField] && (
            <FormHelperText error>{formik.errors[roleField]}</FormHelperText>
          )}
        </Box>

        {channelKey === "email" && enabled && (
          <Box>
            <Typography variant="body2" fontWeight={500} mb={0.5}>
              Other Emails
            </Typography>

            <FormField
              name="other_emails"
              fullWidth
              size="small"
              placeholder="Enter emails separated by comma"
              value={formik.values.other_emails || ""}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />

            <FormHelperText>
              Example: test1@gmail.com, test2@gmail.com
            </FormHelperText>

            <FormHelperText sx={{ color: "#FB9D2C" }}>
              Note: Either Role Permission or Other Emails is required.
            </FormHelperText>

            {formik.touched.other_emails && formik.errors.other_emails && (
              <FormHelperText error>
                {formik.errors.other_emails}
              </FormHelperText>
            )}
          </Box>
        )}

        {channelKey === "in_app" && enabled && (
          <Box>
            <Typography variant="body2" fontWeight={500} mb={0.5}>
              Other Emails
            </Typography>

            <FormField
              name="other_in_app_emails"
              fullWidth
              size="small"
              placeholder="Enter emails separated by comma"
              value={formik.values.other_in_app_emails || ""}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />

            <FormHelperText>
              Example: test1@gmail.com, test2@gmail.com
            </FormHelperText>

            <FormHelperText sx={{ color: "#FB9D2C" }}>
              Note: Either Role Permission or Other Emails is required.
            </FormHelperText>

            {formik.touched.other_in_app_emails &&
              formik.errors.other_in_app_emails && (
                <FormHelperText error>
                  {formik.errors.other_in_app_emails}
                </FormHelperText>
              )}
          </Box>
        )}

        {channelKey === "email" && enabled && (
          <Box>
            <Typography variant="subtitle1" fontWeight={500} mb={0.5}>
              Attachments
            </Typography>

            <Box
              sx={{
                border: "1px solid #e0e0e0",
                borderRadius: "8px",
                px: 2,
                py: 1.5,
                display: "flex",
                alignItems: "center",
                gap: 2,
                backgroundColor: "#fff",
              }}
            >
              <input
                type="file"
                ref={fileInputRef}
                accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
                hidden
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) formik.setFieldValue("files", file);
                  e.target.value = "";
                }}
              />

              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="px-4 py-2 bg-blue-100 text-blue-700 rounded-md"
              >
                Choose File
              </button>

              {!formik.values.files && (
                <Typography variant="body2" color="text.disabled">
                  No file chosen
                </Typography>
              )}

              {formik.values.files && (
                <>
                  <Typography
                    variant="body2"
                    sx={{ maxWidth: "40%" }}
                    noWrap
                    title={fileDisplayName}
                  >
                    {fileDisplayName}
                  </Typography>

                  <Tooltip title="Delete">
                    <span>
                      <IconButton onClick={handleRemoveFile}>
                        <DeleteIconButton bgColor="#FA5151" />
                      </IconButton>
                    </span>
                  </Tooltip>

                  <Tooltip title={hasFile ? "View file" : "No file to preview"}>
                    <span>
                      <IconButton
                        disabled={!hasFile}
                        onClick={() => {
                          const url = getFileUrl();
                          if (url) {
                            setPreviewFileUrl(url);
                            setPreviewOpen(true);
                          }
                        }}
                      >
                        <EyeIconButton
                          bgColor={hasFile ? "#3571FF" : "#b0b0b0"}
                        />
                      </IconButton>
                    </span>
                  </Tooltip>
                </>
              )}
            </Box>

            {formik.errors.files && (
              <FormHelperText error>{formik.errors.files}</FormHelperText>
            )}

            <Typography variant="caption" sx={{ color: "#FB9D2C" }}>
              Supported: PDF, DOC, XLS, JPG, PNG (Max 10MB)
            </Typography>
          </Box>
        )}

        {(channelKey === "sms" || channelKey === "whatsapp") && enabled && (
          <Box>
            <Typography variant="body2" fontWeight={500} mb={0.5}>
              Other Numbers
            </Typography>

            <FormField
              name={
                channelKey === "sms"
                  ? "other_sms_numbers"
                  : "other_whatsapp_numbers"
              }
              fullWidth
              size="small"
              placeholder="Enter numbers with country code"
              value={
                channelKey === "sms"
                  ? formik.values.other_sms_numbers || ""
                  : formik.values.other_whatsapp_numbers || ""
              }
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />

            <FormHelperText>Example: 919876543210, 918888888888</FormHelperText>

            <FormHelperText sx={{ color: "#FB9D2C" }}>
              Note: Either Role Permission or Other Numbers is required.
            </FormHelperText>

            {(channelKey === "sms"
              ? formik.touched.other_sms_numbers &&
                formik.errors.other_sms_numbers
              : formik.touched.other_whatsapp_numbers &&
                formik.errors.other_whatsapp_numbers) && (
              <FormHelperText error>
                {channelKey === "sms"
                  ? formik.errors.other_sms_numbers
                  : formik.errors.other_whatsapp_numbers}
              </FormHelperText>
            )}
          </Box>
        )}

        {channelKey === "whatsapp" && enabled && (
          <Box>
            <Typography variant="body2" fontWeight={500} mb={0.5}>
              Template Name
            </Typography>

            <Select
              fullWidth
              size="small"
              displayEmpty
              value={formik.values.whatsapp_template_name || ""}
              onChange={(e) => {
                const selectedValue = e.target.value;

                // Always reset whatsapp fields first to clear any previously added content
                formik.setFieldValue("whatsapp_template_name", selectedValue);
                formik.setFieldValue("whatsapp_template_variables", []);
                formik.setFieldValue("whatsapp_template_raw", "");
                formik.setFieldValue("whatsapp_message", "");

                if (!selectedValue) return;

                const selectedTemplate = whatsappTemplateOptions.find(
                  (opt) => opt.value === selectedValue,
                );

                if (selectedTemplate?.data) {
                  const matches = selectedTemplate.data.match(/{{\d+}}/g) || [];
                  const uniqueVars = [...new Set(matches)];
                  const variables = new Array(uniqueVars.length).fill("");

                  formik.setFieldValue(
                    "whatsapp_template_variables",
                    variables,
                  );
                  formik.setFieldValue(
                    "whatsapp_template_raw",
                    selectedTemplate.data,
                  );

                  const formatted = formatWhatsAppMessage(
                    selectedTemplate.data,
                    variables,
                  );
                  formik.setFieldValue("whatsapp_message", formatted);
                }
              }}
              input={<OutlinedInput />}
              sx={{
                backgroundColor: "#f9fafb",
                borderRadius: "6px",
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#e5e7eb",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#cbd5e1",
                },
              }}
            >
              <MenuItem value="">
                <Typography variant="body2" color="text.disabled">
                  Select Template
                </Typography>
              </MenuItem>

              {whatsappTemplateOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>

            {formik.touched.whatsapp_template_name &&
              formik.errors.whatsapp_template_name && (
                <FormHelperText error>
                  {formik.errors.whatsapp_template_name}
                </FormHelperText>
              )}
          </Box>
        )}

        <Typography variant="body2" fontWeight={700}>
          {label} Message
        </Typography>

        {subjectField && (
          <Box>
            <Typography variant="body2" fontWeight={500} mb={0.5}>
              Subject
            </Typography>
            <FormField
              name={subjectField}
              fullWidth
              size="small"
              placeholder="Enter Subject"
              value={formik.values[subjectField] ?? ""}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              disabled={!enabled}
            />
            {enabled &&
              formik.touched[subjectField] &&
              formik.errors[subjectField] && (
                <FormHelperText error>
                  {formik.errors[subjectField]}
                </FormHelperText>
              )}
          </Box>
        )}

        <Box>
          <Typography variant="body2" fontWeight={500} mb={0.5}>
            Message
          </Typography>

          <Box
            sx={{
              border: "1px solid #d0d0d0",
              borderRadius: "6px",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              minWidth: 0,
              "& .ql-container.ql-snow": { border: "none" },
              "& .ql-editor": {
                minHeight: "160px",
                padding: "12px",
                fontSize: "0.875rem",
                lineHeight: 1.6,
                wordBreak: "break-word",
                overflowWrap: "break-word",
              },
              "& .ql-editor.ql-blank::before": {
                fontStyle: "normal",
                color: "#bbb",
              },
              "& .ql-toolbar.ql-snow": {
                border: "none",
                borderBottom: "1px solid #e0e0e0",
                backgroundColor: "#fafafa",
                padding: "6px 8px",
                flexWrap: "wrap",
              },
              "& .ql-toolbar .ql-picker-label": { border: "1px solid #ccc" },
              "& .ql-toolbar .ql-picker-options": {
                backgroundColor: "#fff",
                border: "1px solid #ccc",
                boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
                zIndex: 9999,
              },
            }}
          >
            {channelKey !== "in_app" && <QuillToolbar id={toolbarId} />}
            <ReactQuill
              ref={quillRef}
              theme="snow"
              value={formik.values[messageField] ?? ""}
              onChange={(val) => formik.setFieldValue(messageField, val)}
              onFocus={onEditorFocus}
              modules={modules}
              formats={channelKey === "in_app" ? [] : FORMATS}
              placeholder={`Write ${label} message...`}
              readOnly={
                !enabled ||
                (channelKey === "whatsapp" &&
                  !!formik.values.whatsapp_template_name)
              }
            />
          </Box>

          {enabled &&
            formik.touched[messageField] &&
            formik.errors[messageField] && (
              <FormHelperText error>
                {formik.errors[messageField]}
              </FormHelperText>
            )}
        </Box>

        {channelKey === "whatsapp" && enabled ? (
          <Box
            sx={{
              p: 1.5,
              backgroundColor: "#f5f5f5",
              borderRadius: "8px",
              border: "1px solid #e8e8e8",
              minWidth: 0,
            }}
          >
            <Typography variant="body2" fontWeight={700} mb={0.5}>
              Assign Variables
            </Typography>
            <Typography
              variant="caption"
              color="text.disabled"
              display="block"
              mb={2}
            >
              Click on any slot and then assign variable to it, by clicking on
              the available variable listed below
            </Typography>

            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.2, mb: 2 }}>
              {whatsAppVariables.length > 0 ? (
                whatsAppVariables.map((value, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      minWidth: 170,
                      maxWidth: 260,
                      px: 1.25,
                      py: 0.9,
                      borderRadius: "10px",
                      border: "1px solid #d1d5db",
                      backgroundColor: "#fff",
                    }}
                  >
                    <Box
                      sx={{
                        width: 26,
                        height: 26,
                        borderRadius: "50%",
                        display: "grid",
                        placeItems: "center",
                        backgroundColor: value ? "#2563EB" : "#E5E7EB",
                        color: value ? "#FFFFFF" : "#374151",
                        fontSize: "0.75rem",
                        fontWeight: 700,
                        flexShrink: 0,
                      }}
                    >
                      {idx + 1}
                    </Box>
                    <Typography
                      variant="body2"
                      noWrap
                      sx={{
                        flex: 1,
                        color: value ? "#111827" : "#6B7280",
                        fontWeight: 500,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {value || "Empty"}
                    </Typography>
                    {value ? (
                      <CloseIcon
                        sx={{
                          color: "#6B7280",
                          cursor: "pointer",
                          fontSize: "18px",
                        }}
                        onClick={() => onVariableRemove?.(idx)}
                      />
                    ) : null}
                  </Box>
                ))
              ) : (
                <Typography variant="body2" color="text.disabled">
                  Select a WhatsApp template to create variable slots.
                </Typography>
              )}
            </Box>

            <Typography variant="body2" fontWeight={700} mb={0.75}>
              Available Variables
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
              {assignedVariables.map((variable, idx) => (
                <Box
                  key={`${variable}-${idx}`}
                  component="button"
                  type="button"
                  onClick={() => onVariableClick?.(variable, channelKey)}
                  sx={{
                    px: 1.5,
                    py: 0.6,
                    fontSize: "0.75rem",
                    fontFamily: "monospace",
                    border: "1px solid #d0d0d0",
                    borderRadius: "8px",
                    backgroundColor: "#FFFFFF",
                    color: "#374151",
                    cursor: "pointer",
                    transition: "all 0.15s",
                    whiteSpace: "nowrap",
                    "&:hover": {
                      backgroundColor: "#E5E7EB",
                    },
                  }}
                >
                  {variable}
                </Box>
              ))}
            </Box>
          </Box>
        ) : channelKey != "whatsapp" && assignedVariables.length > 0 ? (
          <Box
            sx={{
              p: 1.5,
              backgroundColor: "#f5f5f5",
              borderRadius: "8px",
              border: "1px solid #e8e8e8",
              minWidth: 0,
            }}
          >
            <Typography variant="body2" fontWeight={700} mb={0.5}>
              Available Variables
            </Typography>
            <Typography
              variant="caption"
              color="text.disabled"
              display="block"
              mb={1}
            >
              Click on any variable to insert it into your message:
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
              {assignedVariables.map((variable, idx) => (
                <Box
                  key={idx}
                  component="button"
                  type="button"
                  onClick={() => onVariableClick?.(variable, channelKey)}
                  sx={{
                    px: 1.5,
                    py: 0.5,
                    fontSize: "0.75rem",
                    fontFamily: "monospace",
                    border: "1px solid #d0d0d0",
                    borderRadius: "6px",
                    backgroundColor: "#fff",
                    color: "#444",
                    cursor: "pointer",
                    transition: "all 0.15s",
                    whiteSpace: "nowrap",
                    "&:hover": {
                      backgroundColor: "primary.main",
                      color: "#fff",
                      borderColor: "primary.main",
                    },
                  }}
                >
                  {variable}
                </Box>
              ))}
            </Box>
          </Box>
        ) : null}
      </Box>
    </Box>
  );
};

export default ChannelCard;
